import React from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import Card from 'components/Card';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import PageHeaderIcon from 'components/PageHeaderIcon';
import QuickActions from 'components/QuickActions';
import { useGetAccount } from '../gql/queries/useGetAccount';
import { useGetAccountAnnualsStats } from './gql/queries';
import {
  PageWrapper,
  Container,
  DashboardLayout,
  MainContent,
  Sidebar,
  HeaderContainer,
  HeaderTitle,
  HeaderSubtitle,
  StatsGrid,
  StatCard,
  StatIconWrapper,
  StatContent,
  StatValue,
  StatLabel,
  NavCardsGrid,
  NavCard,
  NavCardHeader,
  NavCardIconWrapper,
  NavCardArrow,
  NavCardTitle,
  NavCardDescription,
  ContentWrapper,
} from './StyledComponents';

// Icons
const CalendarIcon = () => (
  <svg
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="1.5" />
    <line x1="16" y1="2" x2="16" y2="6" strokeWidth="1.5" />
    <line x1="8" y1="2" x2="8" y2="6" strokeWidth="1.5" />
    <line x1="3" y1="10" x2="21" y2="10" strokeWidth="1.5" />
  </svg>
);

const TagIcon = () => (
  <svg
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z"
    />
  </svg>
);

const UsersIcon = () => (
  <svg
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"
    />
    <circle cx="9" cy="7" r="4" strokeWidth="1.5" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"
    />
  </svg>
);

const ReceiptIcon = () => (
  <svg
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9h6m-6 4h6"
    />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 4v16m8-8H4"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 5l7 7-7 7"
    />
  </svg>
);

const isTicketHoss = 'tickethoss' === process.env.REACT_APP_PLATFORM;
const annualLabel = isTicketHoss ? 'Annual Ticket' : 'Annual Pass';
const annualLabelPlural = isTicketHoss ? 'Annual Tickets' : 'Annual Passes';

const AccountAnnualsHubContent = () => {
  const { accountId } = useParams();
  const history = useHistory();
  const location = useLocation();

  // Context-aware routing
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  // Fetch account data
  const { data: accountData, loading: accountLoading } = useGetAccount(
    parseInt(accountId)
  );
  const account = accountData?.getAccount;

  // Fetch stats
  const {
    ticketsCount,
    categoriesCount,
    membersCount,
    purchasesCount,
    loading: statsLoading,
  } = useGetAccountAnnualsStats(parseInt(accountId));

  // Navigation handlers
  const handleNavigate = (path) => {
    history.push(path);
  };

  // Navigation cards config
  const navCards = [
    {
      id: 'tickets',
      title: annualLabelPlural,
      description: `Manage ${annualLabel.toLowerCase()} templates, pricing, and availability`,
      icon: <CalendarIcon />,
      path: `${routePrefix}/annualTickets?account_id=${accountId}`,
    },
    {
      id: 'categories',
      title: 'Categories',
      description: 'Organize tickets into categories for better management',
      icon: <TagIcon />,
      path: `${routePrefix}/annualcategories?account_id=${accountId}`,
    },
    {
      id: 'members',
      title: 'Membership Holders',
      description: 'View and manage annual membership holders',
      icon: <UsersIcon />,
      path: `${routePrefix}/annualMembers/${accountId}/${encodeURIComponent(
        account?.business_dba || ''
      )}`,
    },
    {
      id: 'purchases',
      title: 'Annual Purchases',
      description: `View all ${annualLabel.toLowerCase()} purchasers and transactions`,
      icon: <ReceiptIcon />,
      path: `${routePrefix}/annualUsers/${accountId}/${encodeURIComponent(
        account?.business_dba || ''
      )}`,
    },
  ];

  // Quick actions config
  const quickActions = [
    {
      label: `Create ${annualLabel}`,
      icon: <PlusIcon />,
      onClick: () =>
        handleNavigate(
          `${routePrefix}/annualTickets/add?account_id=${accountId}`
        ),
    },
    {
      label: 'Create Category',
      icon: <PlusIcon />,
      onClick: () =>
        handleNavigate(
          `${routePrefix}/annualcategories?account_id=${accountId}`
        ),
    },
  ];

  // Stats config
  const stats = [
    { label: annualLabelPlural, value: ticketsCount, icon: <CalendarIcon /> },
    { label: 'Categories', value: categoriesCount, icon: <TagIcon /> },
    { label: 'Members', value: membersCount, icon: <UsersIcon /> },
    { label: 'Purchases', value: purchasesCount, icon: <ReceiptIcon /> },
  ];

  const isLoading = accountLoading || statsLoading;

  return (
    <PageWrapper>
      <Container>
        {/* Header Card */}
        <Card style={{ marginBottom: '24px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="events" />
            <div>
              <HeaderTitle>
                {isLoading
                  ? 'Loading...'
                  : `${account?.business_dba || 'Account'} - Annuals Hub`}
              </HeaderTitle>
              <HeaderSubtitle>
                Manage annual tickets, categories, members, and purchases
              </HeaderSubtitle>
            </div>
          </HeaderContainer>
        </Card>

        {/* Stats Cards */}
        <StatsGrid>
          {stats.map((stat) => (
            <StatCard key={stat.label}>
              <StatIconWrapper>{stat.icon}</StatIconWrapper>
              <StatContent>
                <StatValue>{isLoading ? '—' : stat.value}</StatValue>
                <StatLabel>{stat.label}</StatLabel>
              </StatContent>
            </StatCard>
          ))}
        </StatsGrid>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            <Card noPadding>
              <ContentWrapper>
                <NavCardsGrid>
                  {navCards.map((card) => (
                    <NavCard
                      key={card.id}
                      onClick={() => handleNavigate(card.path)}
                    >
                      <NavCardHeader>
                        <NavCardIconWrapper>{card.icon}</NavCardIconWrapper>
                        <NavCardArrow>
                          <ArrowRightIcon />
                        </NavCardArrow>
                      </NavCardHeader>
                      <NavCardTitle>{card.title}</NavCardTitle>
                      <NavCardDescription>
                        {card.description}
                      </NavCardDescription>
                    </NavCard>
                  ))}
                </NavCardsGrid>
              </ContentWrapper>
            </Card>
          </MainContent>

          <Sidebar>
            <QuickActions title="Quick Actions" actions={quickActions} />
          </Sidebar>
        </DashboardLayout>
      </Container>
    </PageWrapper>
  );
};

// Wrap with theme provider
const AccountAnnualsHub = () => (
  <EnhancedThemeProvider>
    <AccountAnnualsHubContent />
  </EnhancedThemeProvider>
);

export default AccountAnnualsHub;
